import { DepressionContentPage } from "@/components/DepressionContentPage";
import type { FAQItem } from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";

const faqs: FAQItem[] = [
  {
    question: "Can anxiety continue after a panic attack?",
    answer:
      "Yes. After a frightening panic episode, fear of another attack, monitoring bodily sensations, and difficulty relaxing can persist. When this continues, an evaluation can help clarify what is happening.",
  },
  {
    question: "Why am I afraid of having another panic attack?",
    answer:
      "A panic attack can feel intense and unpredictable, which can lead to hypervigilance and avoidance. Fear of recurrence is a common part of the experience after a first episode.",
  },
  {
    question: "Can panic attacks affect sleep?",
    answer:
      "Yes. Anxiety and fear of another attack can interfere with falling or staying asleep, and poor sleep can in turn affect how someone feels during the day.",
  },
  {
    question: "Can panic and depression happen together?",
    answer:
      "Yes. Persistent anxiety, loss of sleep, and reduced functioning may occur alongside depressive symptoms. Panic attacks do not directly cause depression, but the two can overlap.",
  },
  {
    question: "What is hypervigilance?",
    answer:
      "Hypervigilance is a state of being constantly on alert, often monitoring for threats or bodily sensations. It can occur after a frightening experience or with ongoing anxiety.",
  },
  {
    question: "How are panic symptoms evaluated?",
    answer:
      "A clinician reviews frequency, triggers, physical symptoms, sleep, mood, trauma history, medical conditions, medications, and functional impairment to understand the full picture.",
  },
  {
    question: "Is TMS used for every type of anxiety?",
    answer:
      "No. TMS is FDA-cleared for certain conditions and may be considered for eligible patients after clinical evaluation. It is not appropriate for every type of anxiety, and candidacy is determined individually.",
  },
];

export default function PanicAnxietyDepression() {
  return (
    <DepressionContentPage
      seo={{
        title: "Panic Attacks, Anxiety & Depression",
        description:
          "A frightening panic episode can be followed by ongoing worry, sleep problems or mood changes. Learn how clinicians evaluate the bigger picture.",
        canonical: "/panic-anxiety-depression",
        keywords:
          "panic attack, anxiety after panic, panic and depression, hypervigilance, mental health evaluation",
      }}
      badge="Mental Health Education"
      heroTitle="When a Panic Attack Changes Everything"
      heroSubtitle="Sometimes the panic attack ends, but the fear of feeling that way again does not."
      heroCtaPrimary="Check My Insurance Options"
      heroCtaPrimaryHref="/check-my-insurance-options"
      sections={[
        {
          heading: "After the Panic Attack Is Over",
          variant: "text",
          body: (
            <>
              <p>
                A panic attack can be frightening, and the experience does not
                always end when the attack itself does. Many people describe
                fear of recurrence, monitoring bodily sensations, trouble
                relaxing, avoidance of places or situations, hypervigilance, and
                disrupted sleep in the days and weeks that follow.
              </p>
              <p>
                This response is common, and it does not mean something is wrong
                with you. But when it persists, it can begin to shape everyday
                life.
              </p>
            </>
          ),
        },
        {
          heading: "When Anxiety Begins Affecting Everyday Life",
          variant: "text",
          body: (
            <p>
              Ongoing worry and hypervigilance can affect concentration, sleep,
              social activity, work, and the ability to relax. When anxiety
              begins limiting what someone feels able to do, it may be worth
              understanding more about what is driving it.
            </p>
          ),
        },
        {
          heading: "Why Depression Can Become Part of the Conversation",
          variant: "text",
          body: (
            <p>
              Persistent anxiety, loss of sleep, and reduced functioning may
              occur alongside depressive symptoms. This does not mean panic
              attacks directly cause depression. But when exhaustion and
              avoidance build up, mood can be affected, and an evaluation can
              help clarify how the pieces fit together.
            </p>
          ),
        },
        {
          heading: "Panic, Trauma and Hypervigilance Can Overlap",
          variant: "text",
          body: (
            <p>
              A frightening experience can sometimes lead to symptoms that
              resemble trauma responses, including hypervigilance and avoidance.
              When panic and trauma-related symptoms overlap, understanding the
              full picture matters.{" "}
              <Link
                to="/when-past-trauma-resurfaces"
                className="text-accent font-semibold hover:underline underline-offset-2"
              >
                Learn more about when past trauma resurfaces.
              </Link>
            </p>
          ),
        },
        {
          heading: "What an Evaluation Looks For",
          variant: "text",
          body: (
            <>
              <p>A clinician may consider:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Frequency and triggers of panic episodes</li>
                <li>Physical symptoms</li>
                <li>Sleep and mood</li>
                <li>Trauma history</li>
                <li>Medical conditions and medications</li>
                <li>Functional impairment</li>
              </ul>
              <p>
                The goal is to understand the pattern before discussing which
                treatment options may be appropriate.
              </p>
            </>
          ),
        },
      ]}
      faq={faqs}
      faqHeading="Frequently Asked Questions"
      relatedLinks={[
        {
          title: "When Past Trauma Resurfaces",
          href: "/when-past-trauma-resurfaces",
        },
        {
          title: "When Anxiety, Insomnia and Depression Overlap",
          href: "/depression-anxiety-insomnia",
        },
        {
          title: "Explore More Mental Health Education",
          href: "/mental-health-resources",
        },
      ]}
      finalCtaHeading="Understanding What Changed Starts With a Conversation"
      finalCtaText="If a panic attack has changed how you feel day to day, an evaluation can help organize what you're experiencing and what options may be appropriate."
      finalCtaPrimaryLabel="Check My Insurance Options"
      finalCtaPrimaryHref="/check-my-insurance-options"
      finalCtaSecondaryLabel="Schedule a Private Consultation"
      finalCtaSecondaryHref="/contactus"
    />
  );
}
